import { barometroCompleto } from './mockdata';
import { buildPublishedEntry } from '../utils/aggregation';
import { normalizeMarketTrend } from '../utils/marketSignals';

const RENTABILIDAD_FIELD = 'haPodidoPercibirCambiosEnSuRentabilidadDebidoALasVariacionesEnElPrecioDelFlete';
const LEGACY_RENTABILIDAD_FIELD = 'haPodidoPercibirCambiosEnSuRentabilidadDebidoALasVariacionesEnELPrecioDelFlete';
const PRICE_TREND_FIELD = 'enComparacionAlUltimoTrimestreDel2024T1ComoHanVariadoLosPreciosDeLosServiciosOfrecidosPorLasNavierasDuranteEsteTrimestre';

const FIELD_MAP = {
  d1_q1: 'comoCalificaLaDisponibilidadDeContenedoresRefrigeradosOEstandarSegunSuNecesidad',
  d1_q2: 'comoCalificaElEstadoDeEstosContenedoresRefrigeradosOEstandarRespectoDelMesAnterior',
  d1_q3: 'queTanFacilFueReservarEspaciosConLasNavierasConLasQueTrabaja',
  d1_q4: 'queEstrategiasHaImplementadoParaSolucionarlo',
  d2_q1: 'consideraQueLasTarifasOfrecidasPorLasNavierasSonJustasEnComparacionConElVolumenDeCargaQueManeja',
  d2_q2: 'haEnfrentadoCostosAdicionalesInesperadosEnLosUltimosMeses',
  d2_q3: 'enCasoDeQueLaRespuestaAnteriorSeaSiEstosCostosAdicionalesEstanDebidamenteJustificados',
  d2_q4: RENTABILIDAD_FIELD,
  d2_q5: PRICE_TREND_FIELD,
  d3_q1: 'comoCalificariaLaClaridadDeLaInformacionProporcionadaPorLasNavierasSobreItinerariosYHorarios',
  d3_q2: 'comoCalificariaLaComunicacionDeLasNavierasAnteEventosImprevistosDemorasODesvios',
  d3_q3: 'queTanAccesiblesSonLasNavierasCuandoNecesitaResolverDudasOProblemasRelacionadosConLosEnvios',
  d3_q5: 'enGeneralComoCalificariaLaCalidadDeLaComunicacionConLasNavierasConLasQueOpera',
  d4_q1: 'consideraQueLosProcedimientosAduanerosSonClaramentePredeciblesYOConsistentes',
  d4_q2: 'comoCalificariaElManejoDeLaMercanciaDuranteLasInspeccionesAduanerasEnTerminosDeCuidadoYPrevencionDeDanos',
  d4_q3: 'consideraQueElPersonalEncargadoDeLasInspeccionesEstaCapacitadoParaManejarSuProductoSinComprometerSuCalidad',
  d4_q4: 'comoEvaluaLaCapacidadOperativaDeLosPuertosEnLosQueOpera',
  d4_q5: 'haExperimentadoCongestionEnLosPuertosEnLosUltimosMeses',
  d4_q6: 'losAccesosYViasDeComunicacionHaciaLosPuertosFacilitanElTransporteDeSuCarga',
  d4_q7: 'consideraQueLaInfraestructuraActualDeLosPuertosEsSuficienteParaLaDemandaExportadoraDeSuSector',
  d4_q8: 'cualEsElPrincipalPuertoEnElQueOpera',
  d3_q4: 'enGeneralComoEvaluariaElNivelDeCumplimientoDeLasNavierasConLasQueTrabaja',
  d3_q6: 'enGeneralComoCalificariaLaRelacionCostoBeneficioDeLosServiciosQueRecibeDeLasNavieras',
};

const BINARY_QUESTION_IDS = new Set(['d2_q2', 'd2_q3', 'd4_q5', 'd4_q7']);
const OPEN_QUESTION_IDS = new Set(['d1_q4', 'd2_q4', 'd4_q8']);

const normalizeBinaryValue = (value) => {
  if (value === null || value === undefined) return '';
  const normalized = value.toString().trim().toLowerCase();
  if (['si', 'sí', 'yes'].includes(normalized)) return 'Sí';
  if (['no'].includes(normalized)) return 'No';
  return value;
};

const formatValueForQuestion = (questionId, rawValue) => {
  if (rawValue === null || rawValue === undefined) return '';
  if (BINARY_QUESTION_IDS.has(questionId)) {
    return normalizeBinaryValue(rawValue);
  }
  if (OPEN_QUESTION_IDS.has(questionId)) {
    return rawValue.toString();
  }
  if (typeof rawValue === 'number') {
    return rawValue.toString();
  }
  const trimmed = rawValue.toString().trim();
  const parsed = parseInt(trimmed, 10);
  if (!Number.isNaN(parsed)) {
    return parsed.toString();
  }
  return trimmed;
};

const slugifyCompany = (value) => (value || '')
  .toString()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const normalizePortName = (value) => {
  if (!value) return 'Otro puerto';
  const normalized = value.toString().trim().toLowerCase();
  const rules = [
    { regex: /(dp\s*world|posorja|do world)/i, label: 'DP World (Posorja)' },
    { regex: /contecon/i, label: 'Contecon Guayaquil' },
    { regex: /tpg/i, label: 'Terminal Portuario Guayaquil (TPG)' },
    { regex: /yieldport|yilport/i, label: 'Yilport' },
    { regex: /naportec|na portec/i, label: 'Naportec' },
  ];
  const found = rules.find((rule) => rule.regex.test(normalized));
  if (found) return found.label;
  if (/guayaquil/i.test(normalized)) return 'Puerto de Guayaquil';
  return value.toString().trim();
};

const buildDatasetEntities = () => {
  const userMap = new Map();
  const userCounts = {};

  const submissions = barometroCompleto.map((entry, index) => {
    const companyName = entry.nombreDeLaEmpresa?.trim() || `Empresa ${index + 1}`;
    const baseSlug = slugifyCompany(companyName) || `empresa_${index + 1}`;
    let companyId = baseSlug || `empresa_${index + 1}`;
    if (userMap.has(companyId) && userMap.get(companyId).name !== companyName) {
      companyId = `${baseSlug}_${index}`;
    }

    const formData = {};
    Object.entries(FIELD_MAP).forEach(([questionId, fieldName]) => {
      const raw =
        questionId === 'd2_q4'
          ? entry[RENTABILIDAD_FIELD] ?? entry[LEGACY_RENTABILIDAD_FIELD]
          : entry[fieldName];
      const formatted = formatValueForQuestion(questionId, raw);
      if (formatted !== '') {
        formData[questionId] = formatted;
      }
    });

    const month = entry.submitDateUtc ? entry.submitDateUtc.slice(0, 7) : '2025-07';

    const submission = {
      docId: `${companyId}_${month}_${index}`,
      companyId,
      companyName,
      exportType: entry.productoQueExporta || 'Otros',
      otherExportTypeDetail: entry.enCasoDeQueEligioOtrosMencioneElProductoQueExporta || '',
      month,
      status: 'approved',
      data: formData,
      observations: {},
      attachments: {},
      destination: entry.destinoPrincipalDeExportaciones || 'No especificado',
      contactEmail: entry.contactoCorreoElectronico || '',
      respondent: entry.nombreDeQuienResponde || '',
      trend: normalizeMarketTrend(entry[PRICE_TREND_FIELD]),
      rentabilidadTrend: entry[RENTABILIDAD_FIELD] ?? entry[LEGACY_RENTABILIDAD_FIELD] ?? '',
      port: normalizePortName(entry.cualEsElPrincipalPuertoEnElQueOpera),
    };

    userCounts[companyId] = (userCounts[companyId] || 0) + 1;

    if (!userMap.has(companyId)) {
      userMap.set(companyId, {
        id: companyId,
        name: companyName,
        exportType: submission.exportType,
        destination: submission.destination,
        contactEmail: submission.contactEmail,
        respondent: submission.respondent,
        role: 'company',
        stars: 0,
      });
    }

    return submission;
  });

  const users = [
    { id: 'admin_user', name: 'Administrador', role: 'admin', stars: 0 },
    ...Array.from(userMap.values()).map((user) => ({
      ...user,
      stars: Math.min(10, userCounts[user.id] || 1),
    })),
  ];

  return { submissions, users };
};

const getLastDayOfMonthIso = (monthKey) => {
  const [yearStr, monthStr] = monthKey.split('-');
  const date = new Date(Date.UTC(parseInt(yearStr, 10), parseInt(monthStr, 10), 0));
  return date.toISOString().slice(0, 10);
};

const buildInitialPublishedHistory = (submissions) => {
  const grouped = submissions.reduce((acc, submission) => {
    if (submission.status !== 'approved') return acc;
    if (!acc[submission.month]) acc[submission.month] = [];
    acc[submission.month].push(submission);
    return acc;
  }, {});

  return Object.keys(grouped).reduce((acc, month) => {
    const entry = buildPublishedEntry(grouped[month], month);
    if (entry) acc[month] = entry;
    return acc;
  }, {});
};

const buildInitialDeadlines = (submissions) => {
  const map = submissions.reduce((acc, submission) => {
    if (!acc[submission.month]) {
      acc[submission.month] = getLastDayOfMonthIso(submission.month);
    }
    return acc;
  }, {});

  const currentMonthKey = new Date().toISOString().slice(0, 7);
  if (!map[currentMonthKey]) {
    map[currentMonthKey] = getLastDayOfMonthIso(currentMonthKey);
  }
  return map;
};

export const datasetEntities = buildDatasetEntities();
export const initialPublishedHistory = buildInitialPublishedHistory(datasetEntities.submissions);
export const initialDeadlinesMap = buildInitialDeadlines(datasetEntities.submissions);
